import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, ShieldAlert, LibraryBig, X, Wand2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import AISuggestRisksButton from "./AISuggestRisksButton";
import MitigationCoachButton from "./MitigationCoachButton";

const LIKELIHOOD = ["Low", "Medium", "High"];
const IMPACT = ["Low", "Medium", "High"];
const STATUS = ["Open", "Mitigated", "Closed"];

const sevTone = (lvl) => ({
  Low: "text-emerald-300",
  Medium: "text-amber-300",
  High: "text-rose-300",
}[lvl] || "text-slate-300");

const statusTone = (s) => ({
  Open: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  Mitigated: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Closed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
}[s] || "bg-slate-700/40 text-slate-300 border-slate-600");

export default function RiskRegister({ projectId, tasks, locked, onChanged }) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [libLoading, setLibLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/risks`);
      setRisks(data || []);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openLibrary = async () => {
    if (locked) { toast.error("Upgrade to Compass PM Pro to use this feature"); return; }
    setLibraryOpen(true);
    setSelected(new Set());
    setLibLoading(true);
    try {
      const { data } = await api.get("/risk-templates");
      setLibrary(data || []);
    } catch (e) { toast.error(errMsg(e)); }
    setLibLoading(false);
  };

  const applyTemplates = async ({ auto }) => {
    try {
      const body = { data: auto ? { auto_match: true } : { template_ids: [...selected] } };
      const { data } = await api.post(`/projects/${projectId}/risks/apply-templates`, body);
      if (data.count === 0) { toast.message("No matching risk templates to seed."); return; }
      toast.success(`Added ${data.count} risk${data.count === 1 ? "" : "s"} from library`);
      setLibraryOpen(false);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const taskOptions = (tasks || [])
    .slice()
    .sort((a, b) => Number(a.task_number || 0) - Number(b.task_number || 0))
    .map((t) => ({ v: String(t.task_number), l: `#${t.task_number} ${t.name || ""}`.trim() }));

  const addRisk = async () => {
    if (locked) { toast.error("Upgrade to Compass PM Pro to use this feature"); return; }
    try {
      const { data } = await api.post(`/projects/${projectId}/risks`, {
        data: { description: "New risk", likelihood: "Medium", impact: "Medium", status: "Open" },
      });
      setRisks((x) => [...x, data]);
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const updateRisk = async (riskId, patch) => {
    setRisks((xs) => xs.map((r) => (r.id === riskId ? { ...r, ...patch } : r)));
    try {
      await api.put(`/projects/${projectId}/risks/${riskId}`, { data: patch });
      // likelihood/impact/status changes feed the risk score
      if (patch.likelihood || patch.impact || patch.status) onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const removeRisk = async (riskId) => {
    if (!window.confirm("Delete this risk?")) return;
    try {
      await api.delete(`/projects/${projectId}/risks/${riskId}`);
      setRisks((xs) => xs.filter((r) => r.id !== riskId));
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="pc-card" data-testid="risk-register-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-rose-300" />
          <div>
            <div className="text-sm font-semibold text-white" style={{ fontFamily: "Outfit" }}>Risk Register</div>
            <div className="text-xs text-slate-400">Track project risks, link them to tasks, and plan mitigations. Open risks feed the project risk score.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AISuggestRisksButton
            projectId={projectId}
            locked={locked}
            onLocked={() => toast.error("Upgrade to Compass PM Pro to use AI risk suggestions")}
            onAdded={() => { load(); onChanged && onChanged(); }}
          />
          <button
            onClick={openLibrary}
            disabled={locked}
            data-testid="load-from-library-btn"
            className={`inline-flex items-center gap-2 border border-slate-700 hover:border-rose-500 text-slate-200 px-3 py-2 rounded-lg text-sm ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <LibraryBig size={14} /> Load from library
          </button>
          <button
            onClick={addRisk}
            disabled={locked}
            data-testid="add-risk-btn"
            className={`inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-rose-700 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg shadow-rose-900/30 ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <Plus size={14} /> Add risk
          </button>
        </div>
      </div>

      {libraryOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="risk-library-modal">
          <div className="pc-card w-full max-w-3xl mt-16">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <LibraryBig size={16} className="text-rose-300" />
                <div>
                  <div className="text-sm font-semibold text-white" style={{ fontFamily: "Outfit" }}>Risk library</div>
                  <div className="text-xs text-slate-400">Pick templates to clone into this project — or auto-match by program and roles.</div>
                </div>
              </div>
              <button onClick={() => setLibraryOpen(false)} data-testid="close-risk-library-btn" className="text-slate-500 hover:text-white"><X size={16} /></button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto px-2">
              {libLoading && <div className="p-8 text-center text-slate-400">Loading library…</div>}
              {!libLoading && library.length === 0 && (
                <div className="p-10 text-center text-slate-400">
                  No templates saved yet. <Link to="/app/risk-templates" className="text-blue-400 hover:text-blue-300">Open the Risk Library →</Link>
                </div>
              )}
              {!libLoading && library.map((t) => {
                const checked = selected.has(t.id);
                return (
                  <label key={t.id} data-testid={`library-row-${t.id}`} className={`flex items-start gap-3 p-3 border-b border-slate-800/60 cursor-pointer ${checked ? "bg-rose-500/5" : ""}`}>
                    <input type="checkbox" checked={checked} onChange={(e) => {
                      setSelected((prev) => { const n = new Set(prev); e.target.checked ? n.add(t.id) : n.delete(t.id); return n; });
                    }} className="mt-1" data-testid={`library-check-${t.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">{t.name || "Untitled"}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${t.likelihood === "High" ? "text-rose-300 border-rose-500/40" : t.likelihood === "Low" ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`}>L: {t.likelihood}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${t.impact === "High" ? "text-rose-300 border-rose-500/40" : t.impact === "Low" ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`}>I: {t.impact}</span>
                        {t.role_id && <span className="text-[10px] text-slate-400">role-scoped</span>}
                        {t.program_id && <span className="text-[10px] text-slate-400">program-scoped</span>}
                      </div>
                      {t.description && <div className="text-xs text-slate-300 mt-1">{t.description}</div>}
                      {t.mitigation && <div className="text-xs text-slate-500 mt-1">↳ {t.mitigation}</div>}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-slate-800">
              <button onClick={() => applyTemplates({ auto: true })} data-testid="apply-auto-btn" className="inline-flex items-center gap-2 border border-slate-700 hover:border-rose-500 text-slate-200 px-3 py-2 rounded-lg text-sm">
                <Wand2 size={14} /> Auto-match for this project
              </button>
              <div className="flex gap-2">
                <button onClick={() => setLibraryOpen(false)} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
                <button onClick={() => applyTemplates({ auto: false })} disabled={selected.size === 0} data-testid="apply-selected-btn"
                  className={`bg-gradient-to-r from-rose-500 to-rose-700 text-white px-4 py-2 rounded-lg text-sm font-medium ${selected.size === 0 ? "opacity-50 cursor-not-allowed" : ""}`}>
                  Add {selected.size} to project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="risks-table">
          <thead className="text-left text-xs uppercase tracking-widest text-blue-300/80 border-b border-slate-800">
            <tr>
              <th className="p-3 w-28">Task #</th>
              <th className="p-3">Description</th>
              <th className="p-3 w-28">Likelihood</th>
              <th className="p-3 w-28">Impact</th>
              <th className="p-3">Mitigation</th>
              <th className="p-3 w-40">Owner</th>
              <th className="p-3 w-32">Status</th>
              <th className="p-3 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {loading && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">Loading risks…</td></tr>
            )}
            {!loading && risks.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-slate-400" data-testid="risks-empty">
                No risks logged yet — click "Add risk" to capture one.
              </td></tr>
            )}
            {!loading && risks.map((r) => (
              <tr key={r.id} data-testid={`risk-row-${r.id}`} className="hover:bg-slate-900/40">
                <td className="p-2">
                  <Select
                    value={r.task_number != null ? String(r.task_number) : ""}
                    onChange={(v) => updateRisk(r.id, { task_number: v ? Number(v) : null })}
                    options={[{ v: "", l: "— None —" }, ...taskOptions]}
                    testid={`risk-task-${r.id}`}
                  />
                </td>
                <td className="p-2">
                  <Inline
                    value={r.description || ""}
                    onChange={(v) => updateRisk(r.id, { description: v })}
                    placeholder="Describe the risk"
                    testid={`risk-description-${r.id}`}
                  />
                </td>
                <td className="p-2">
                  <Select
                    value={r.likelihood || "Medium"}
                    onChange={(v) => updateRisk(r.id, { likelihood: v })}
                    options={LIKELIHOOD.map((o) => ({ v: o, l: o }))}
                    tone={sevTone(r.likelihood)}
                    testid={`risk-likelihood-${r.id}`}
                  />
                </td>
                <td className="p-2">
                  <Select
                    value={r.impact || "Medium"}
                    onChange={(v) => updateRisk(r.id, { impact: v })}
                    options={IMPACT.map((o) => ({ v: o, l: o }))}
                    tone={sevTone(r.impact)}
                    testid={`risk-impact-${r.id}`}
                  />
                </td>
                <td className="p-2">
                  <div className="flex items-center gap-1">
                    <Inline
                      value={r.mitigation || ""}
                      onChange={(v) => updateRisk(r.id, { mitigation: v })}
                      placeholder="Mitigation plan"
                      testid={`risk-mitigation-${r.id}`}
                    />
                    <MitigationCoachButton
                      projectId={projectId}
                      risk={r}
                      locked={locked}
                      onLocked={() => toast.error("Upgrade to Compass PM Pro to use the mitigation coach")}
                      onApplied={(text) => setRisks((xs) => xs.map((x) => x.id === r.id ? { ...x, mitigation: text } : x))}
                    />
                  </div>
                </td>
                <td className="p-2">
                  <Inline
                    value={r.owner || ""}
                    onChange={(v) => updateRisk(r.id, { owner: v })}
                    placeholder="Owner"
                    testid={`risk-owner-${r.id}`}
                  />
                </td>
                <td className="p-2">
                  <select
                    value={r.status || "Open"}
                    onChange={(e) => updateRisk(r.id, { status: e.target.value })}
                    data-testid={`risk-status-${r.id}`}
                    className={`w-full px-2 py-1 rounded-md border text-xs font-medium outline-none ${statusTone(r.status)}`}
                  >
                    {STATUS.map((s) => <option key={s} value={s} className="bg-slate-900 text-white">{s}</option>)}
                  </select>
                </td>
                <td className="p-2 text-right">
                  <button onClick={() => removeRisk(r.id)} data-testid={`delete-risk-${r.id}`} className="text-slate-500 hover:text-rose-400">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Inline({ value, onChange, placeholder = "", testid }) {
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 text-sm outline-none text-slate-200 placeholder:text-slate-500"
    />
  );
}

function Select({ value, onChange, options, tone = "text-slate-200", testid }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`w-full bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 text-sm outline-none ${tone}`}
    >
      {options.map((o) => <option key={o.v} value={o.v} className="bg-slate-900 text-white">{o.l}</option>)}
    </select>
  );
}
